import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { PageHead, Skel, Empty, Stat } from '../components/ui'
import { money, number, thaiDate } from '../lib/format'
import { useAuth } from '../auth'

/**
 * Enterprise analytics (part 45). The server owns every aggregate: the monthly series comes
 * from a materialized view, and a cohort is evaluated against findings on read. This page only
 * composes those results, so a definition saved by one user is interpreted identically for the
 * next one.
 */

type Severity = 'critical' | 'high' | 'medium' | 'low'

interface Kpis {
  findings_per_month: number
  amount_per_month_satang: number
  avg_risk: number
  total_findings: number
  total_amount_satang: number
  months: number
  clients: number
  critical: number
  high: number
}

interface MonthPoint {
  month: string
  findings: number
  amount_satang: number
  findings_delta: number
  amount_delta: number
}

interface ClientTotal {
  client_id: string
  client_name: string
  findings: number
  amount_satang: number
  high_findings: number
  share_pct: number
}

interface Overview {
  generated_at: string
  kpis: Kpis
  trend: MonthPoint[]
  top_clients: ClientTotal[]
  cohort_count: number
}

interface CohortDefinition {
  created_after?: string
  min_findings: number
  severities?: string[]
}

interface Cohort {
  id: string
  name: string
  definition: CohortDefinition
  created_at: string
  members: number
  findings: number
  amount_satang: number
  retention_months: number
}

const SEVERITIES: { id: Severity; label: string; tone: string }[] = [
  { id: 'critical', label: 'วิกฤต', tone: 'var(--color-crimson)' },
  { id: 'high', label: 'สูง', tone: 'var(--color-amber)' },
  { id: 'medium', label: 'กลาง', tone: 'var(--color-gold)' },
  { id: 'low', label: 'ต่ำ', tone: 'var(--color-ivory-dim)' },
]

const severityLabel: Record<string, string> = {
  critical: 'วิกฤต',
  high: 'สูง',
  medium: 'กลาง',
  low: 'ต่ำ',
}

const monthLabel = (m: string) => {
  const [y, mm] = m.split('-')
  return `${['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'][Number(mm) - 1]} ${String(Number(y) + 543).slice(2)}`
}

function definitionLabel(d: CohortDefinition): string {
  const parts: string[] = []
  if (d.created_after) parts.push(`ตั้งแต่ ${thaiDate(d.created_after)}`)
  if (d.min_findings > 0) parts.push(`อย่างน้อย ${d.min_findings} ข้อสังเกต`)
  if (d.severities && d.severities.length > 0) {
    parts.push(d.severities.map((s) => severityLabel[s] ?? s).join(' / '))
  }
  return parts.length === 0 ? 'ทุกข้อสังเกตขององค์กร' : parts.join(' · ')
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function Analytics() {
  const qc = useQueryClient()
  const { me } = useAuth()
  const canRefresh = me?.role === 'owner' || me?.role === 'admin'

  const [name, setName] = useState('')
  const [createdAfter, setCreatedAfter] = useState('')
  const [minFindings, setMinFindings] = useState('1')
  const [severities, setSeverities] = useState<Severity[]>([])
  const [notice, setNotice] = useState<string | null>(null)

  const overview = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: () => api<Overview>('/v1/analytics/overview'),
    staleTime: 60_000,
  })

  const cohorts = useQuery({
    queryKey: ['analytics-cohorts'],
    queryFn: () => list<Cohort>('/v1/analytics/cohorts'),
    staleTime: 30_000,
  })

  const create = useMutation({
    mutationFn: () => apiWrite<Cohort>('/v1/analytics/cohorts', 'POST', {
      name: name.trim(),
      created_after: createdAfter,
      min_findings: Number(minFindings) || 0,
      severities,
    }),
    onSuccess: (cohort) => {
      setNotice(`บันทึกกลุ่ม “${cohort.name}” แล้ว · พบ ${number(cohort.members)} ราย`)
      setName('')
      setCreatedAfter('')
      setMinFindings('1')
      setSeverities([])
      void qc.invalidateQueries({ queryKey: ['analytics-cohorts'] })
      void qc.invalidateQueries({ queryKey: ['analytics-overview'] })
    },
  })

  const refresh = useMutation({
    mutationFn: () => apiWrite<{ refreshed_at: string }>('/v1/analytics/refresh', 'POST'),
    onSuccess: (result) => {
      setNotice(`รีเฟรชข้อมูลรายเดือนแล้วเมื่อ ${thaiDate(result.refreshed_at)}`)
      void qc.invalidateQueries({ queryKey: ['analytics-overview'] })
    },
  })

  const k = overview.data?.kpis
  const trend = overview.data?.trend ?? []
  const topClients = overview.data?.top_clients ?? []
  const cohortCount = overview.data?.cohort_count ?? 0
  const rows = cohorts.data ?? []
  const maxTrend = useMemo(() => Math.max(1, ...trend.map((t) => t.findings)), [trend])
  const maxClientAmount = useMemo(
    () => Math.max(1, ...topClients.map((c) => c.amount_satang)),
    [topClients],
  )

  const toggleSeverity = (id: Severity) => {
    setSeverities((current) => (
      current.includes(id) ? current.filter((s) => s !== id) : [...current, id]
    ))
  }

  const canSubmit = name.trim().length > 0 && !create.isPending

  return (
    <>
      <PageHead
        title="วิเคราะห์องค์กร"
        sub="ภาพรวมแนวโน้มและกลุ่มลูกค้าที่มีความเสี่ยง · ตัวเลขทั้งหมดคำนวณจากข้อสังเกตขององค์กรนี้"
        actions={
          <div className="row" style={{ gap: 8 }}>
            {refresh.data?.refreshed_at ? (
              <span className="chip gold">รีเฟรช {thaiDate(refresh.data.refreshed_at)}</span>
            ) : null}
            {canRefresh ? (
              <button
                className="btn btn-ghost btn-sm"
                disabled={refresh.isPending}
                onClick={() => { setNotice(null); refresh.mutate() }}
              >
                {refresh.isPending ? 'กำลังรีเฟรช…' : 'รีเฟรชข้อมูลรายเดือน'}
              </button>
            ) : null}
          </div>
        }
      />

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 4, marginBottom: 16, padding: '10px 14px', fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)', borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}

      {refresh.isError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(refresh.error, 'รีเฟรชข้อมูลไม่สำเร็จ')}
        </div>
      ) : null}

      {overview.isLoading ? (
        <div className="stack"><Skel h={120} /><Skel h={220} /></div>
      ) : overview.isError ? (
        <div className="card card-pad">
          <div className="form-error">{errorText(overview.error, 'โหลดข้อมูลวิเคราะห์ไม่สำเร็จ')}</div>
        </div>
      ) : !k ? (
        <div className="stack"><Skel h={120} /><Skel h={220} /></div>
      ) : (
        <>
          <div className="grid-4 stagger">
            <Stat
              label="ข้อสังเกตเฉลี่ยต่อเดือน"
              value={number(k.findings_per_month)}
              unit="รายการ"
              foot={`รวม ${number(k.total_findings)} รายการใน ${k.months} เดือน`}
            />
            <Stat
              label="มูลค่าเฉลี่ยต่อเดือน"
              value={money(k.amount_per_month_satang, { compact: true })}
              foot={`รวม ${money(k.total_amount_satang, { compact: true })}`}
            />
            <Stat
              label="ความเสี่ยงเฉลี่ย"
              value={k.avg_risk.toFixed(1)}
              unit="/ 100"
              foot={`${number(k.critical)} วิกฤต · ${number(k.high)} สูง`}
            />
            <Stat
              label="ลูกค้าที่มีข้อสังเกต"
              value={number(k.clients)}
              unit="ราย"
              foot={`บันทึกกลุ่มไว้ ${number(cohortCount)} กลุ่ม`}
            />
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">แนวโน้มรายเดือน</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                {k.months > 12 ? 'แสดง 12 เดือนล่าสุด' : 'ทุกเดือนที่มีข้อมูล'} · เวลา UTC
              </span>
            </div>
            {trend.length === 0 ? (
              <div style={{ marginTop: 14 }}>
                <Empty title="ยังไม่มีข้อมูลรายเดือน" body="เมื่อมีข้อสังเกต ระบบจะสรุปยอดรายเดือนให้ที่นี่" />
              </div>
            ) : (
              <div className="row" style={{ alignItems: 'flex-end', gap: 14, height: 150, marginTop: 18 }}>
                {trend.map((t) => (
                  <div key={t.month} style={{ flex: 1, textAlign: 'center' }}>
                    <div
                      title={`${number(t.findings)} รายการ · ${money(t.amount_satang, { compact: true })}`}
                      style={{ height: 96, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
                    >
                      {t.findings > 0 ? (
                        <div
                          style={{
                            width: 30,
                            height: `${Math.max(6, (t.findings / maxTrend) * 96)}px`,
                            background: 'var(--color-gold)',
                            opacity: 0.78,
                            borderRadius: 2,
                          }}
                        />
                      ) : null}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 6 }}>{number(t.findings)}</div>
                    <div className="dim" style={{ fontSize: 10.5, marginTop: 2 }}>{monthLabel(t.month)}</div>
                    <div
                      className="dim tnum"
                      style={{ fontSize: 10, marginTop: 2, color: t.findings_delta < 0 ? 'var(--color-emerald-soft)' : undefined }}
                    >
                      {t.findings_delta === 0 ? '—' : `${t.findings_delta > 0 ? '+' : ''}${number(t.findings_delta)}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">ลูกค้าที่มูลค่าความเสี่ยงสูงสุด</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                สัดส่วนคิดจากมูลค่าความเสี่ยงรวมของทุกลูกค้า
              </span>
            </div>
            {topClients.length === 0 ? (
              <div style={{ marginTop: 14 }}>
                <Empty title="ยังไม่มีข้อมูลลูกค้า" body="เมื่อมีข้อสังเกตที่ผูกกับลูกค้า อันดับจะปรากฏที่นี่" />
              </div>
            ) : (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>ลูกค้า</th>
                      <th style={{ textAlign: 'right' }}>ข้อสังเกต</th>
                      <th style={{ textAlign: 'right' }}>วิกฤต/สูง</th>
                      <th style={{ textAlign: 'right' }}>มูลค่า</th>
                      <th style={{ minWidth: 140 }}>สัดส่วน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topClients.map((c) => (
                      <tr key={c.client_id} style={{ cursor: 'default' }}>
                        <td>{c.client_name || 'ไม่ระบุชื่อ'}</td>
                        <td className="num tnum">{number(c.findings)}</td>
                        <td className="num tnum">{number(c.high_findings)}</td>
                        <td className="num tnum">{money(c.amount_satang, { compact: true })}</td>
                        <td>
                          <div className="row" style={{ gap: 8 }}>
                            <span className="bar" style={{ flex: 1, height: 5 }}>
                              <i style={{
                                width: `${Math.max(2, (c.amount_satang / maxClientAmount) * 100)}%`,
                                background: 'var(--color-gold)',
                              }} />
                            </span>
                            <span className="dim tnum" style={{ width: 42, textAlign: 'right', fontSize: 11 }}>
                              {c.share_pct.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">สร้างกลุ่มวิเคราะห์</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เงื่อนไขจะถูกบันทึกไว้และประเมินใหม่ทุกครั้งที่เปิดหน้า
              </span>
            </div>

            <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 14 }}>
              <div className="field" style={{ flex: '1 1 240px', marginTop: 0 }}>
                <label htmlFor="cohort-name">ชื่อกลุ่ม</label>
                <input
                  id="cohort-name"
                  className="input"
                  maxLength={120}
                  placeholder="เช่น ลูกค้าที่มีความเสี่ยงสูงต่อเนื่อง"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="field" style={{ width: 170, marginTop: 0 }}>
                <label htmlFor="cohort-after">ข้อมูลตั้งแต่</label>
                <input
                  id="cohort-after"
                  className="input"
                  type="date"
                  value={createdAfter}
                  onChange={(e) => setCreatedAfter(e.target.value)}
                />
              </div>
              <div className="field" style={{ width: 130, marginTop: 0 }}>
                <label htmlFor="cohort-min">ขั้นต่ำ (รายการ)</label>
                <input
                  id="cohort-min"
                  className="input tnum"
                  type="number"
                  min={0}
                  max={100000}
                  value={minFindings}
                  onChange={(e) => setMinFindings(e.target.value)}
                />
              </div>
              <div className="field" style={{ flex: '1 1 300px', marginTop: 0 }}>
                <label>ระดับความรุนแรง</label>
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {SEVERITIES.map((s) => {
                    const on = severities.includes(s.id)
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={`chip ${on ? 'chip-on' : ''}`}
                        aria-pressed={on}
                        onClick={() => toggleSeverity(s.id)}
                        style={{ cursor: 'pointer', color: on ? s.tone : undefined }}
                      >
                        {s.label}
                      </button>
                    )
                  })}
                  {severities.length === 0 ? (
                    <span className="dim" style={{ fontSize: 10.5, alignSelf: 'center' }}>
                      ไม่เลือก = ทุกระดับ
                    </span>
                  ) : null}
                </div>
              </div>
              <button
                className="btn btn-primary"
                disabled={!canSubmit}
                onClick={() => { setNotice(null); create.mutate() }}
              >
                {create.isPending ? 'กำลังบันทึก…' : 'บันทึกกลุ่ม'}
              </button>
            </div>

            {create.isError ? (
              <div className="form-error" style={{ marginTop: 12 }}>
                {errorText(create.error, 'บันทึกกลุ่มไม่สำเร็จ')}
              </div>
            ) : null}
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">กลุ่มที่บันทึกไว้</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                สมาชิกคือลูกค้าที่ผ่านเงื่อนไข · ระยะเวลาคงอยู่คือเดือนแรกถึงเดือนสุดท้ายที่มีกิจกรรม
              </span>
            </div>

            {cohorts.isLoading ? (
              <div style={{ marginTop: 14 }}><Skel h={140} /></div>
            ) : cohorts.isError ? (
              <div className="form-error" style={{ marginTop: 14 }}>
                {errorText(cohorts.error, 'โหลดกลุ่มไม่สำเร็จ')}
              </div>
            ) : rows.length === 0 ? (
              <div style={{ marginTop: 14 }}>
                <Empty title="ยังไม่มีกลุ่มที่บันทึกไว้" body="สร้างกลุ่มแรกจากเงื่อนไขด้านบน แล้วระบบจะคำนวณสมาชิกให้ทันที" />
              </div>
            ) : (
              <div className="table-wrap" style={{ marginTop: 12 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>กลุ่ม</th>
                      <th>เงื่อนไข</th>
                      <th style={{ textAlign: 'right' }}>สมาชิก</th>
                      <th style={{ textAlign: 'right' }}>ข้อสังเกต</th>
                      <th style={{ textAlign: 'right' }}>มูลค่า</th>
                      <th style={{ textAlign: 'right' }}>คงอยู่ (เดือน)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => (
                      <tr key={c.id} style={{ cursor: 'default' }}>
                        <td>
                          {c.name}
                          <div className="dim" style={{ fontSize: 10.5 }}>สร้าง {thaiDate(c.created_at)}</div>
                        </td>
                        <td className="dim" style={{ fontSize: 11.5 }}>{definitionLabel(c.definition)}</td>
                        <td className="num tnum">{number(c.members)}</td>
                        <td className="num tnum">{number(c.findings)}</td>
                        <td className="num tnum">{money(c.amount_satang, { compact: true })}</td>
                        <td className="num tnum">{c.retention_months > 0 ? number(c.retention_months) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="dim" style={{ marginTop: 14, fontSize: 11.5 }}>
            ข้อมูลรวมรายเดือนสร้างจาก materialized view และรีเฟรชเมื่อผู้ดูแลสั่งเท่านั้น ·
            ตัวเลขลูกค้าเป็นค่าประมาณจากข้อสังเกตที่ยังไม่ถูกปิด
          </div>
        </>
      )}
    </>
  )
}
